import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Box, Button, Tab, Tabs } from "@mui/material";
import "./index.scss";
import { getOfflineData } from "../../utils/offline-services";
import Setup from "../setup";
import { CFV2FormsStatusTabs } from "../cf-v2-forms-status-tabs";
import CustomModal from "../../../common-utilities/core/modal";
import CreateCfV2Form from "../cf-v2-create-form";
import { CfV2Report } from "../cf-v2-dashboard";
import { CfV2PipelineDashboard } from "../cf-v2-pipeline-report";
import { setHomeTab } from "../../../../reducer/cfApprovalV2";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";

const CF_TAB_CONFIG = {
  cfAll: { status: null, countKey: "all" },
  cfPending: { status: "IN_PROGRESS", countKey: "inprogress" },
  cfApproved: { status: "APPROVED", countKey: "approved" },
  cfDropped: { status: "DROPPED", countKey: "dropped" },
  cfExceptionPending: { status: "EXCEPTION_PENDING", countKey: "exceptionPending" },
};

const CFApprovalv2Home = () => {
  const dispatch = useDispatch();
  const homeTab = useSelector((state) => state.cfApprovalV2.homeTab ?? "dashboard");
  const appliedFilters = useSelector((state) => state.cfApprovalV2.filters);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cfFormCounts, setCfFormCounts] = useState({ all: 0, approved: 0, inprogress: 0, dropped: 0, exceptionPending: 0 });
  const userDetails = getOfflineData("user") || {};
  const role = userDetails?.role?.toUpperCase();
  const isAdmin = role === "ADMIN";
  const isBDLead = role === "BD_LEAD";

  useEffect(() => {
    invokeApi(
      HTTP_METHODS.GET,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_APPROVALS_COUNTS}`,
      null,
      appliedFilters,
    )
      .then((counts) => {
        setCfFormCounts({
          all: counts.all || 0,
          approved: counts.approved || 0,
          inprogress: counts.inprogress || 0,
          dropped: counts.dropped || 0,
          exceptionPending: counts.exceptionPending || 0,
        });
      })
      .catch(() => {
        setCfFormCounts({ all: 0, approved: 0, inprogress: 0, dropped: 0, exceptionPending: 0 });
      });
  }, [appliedFilters, refreshKey]);

  const tabs = [
    { key: "dashboard", label: "Dashboard", visible: true },
    { key: "reports", label: "Reports", visible: true },
    { key: "cfAll", label: "All Sites", visible: true, count: cfFormCounts.all },
    { key: "cfPending", label: "Pending Sites", visible: !isAdmin, count: cfFormCounts.inprogress },
    { key: "cfExceptionPending", label: "Exception Pending", visible: !isAdmin, count: cfFormCounts.exceptionPending },
    { key: "cfApproved", label: "Approved Sites", visible: !isAdmin, count: cfFormCounts.approved },
    { key: "cfDropped", label: "Dropped Sites", visible: isBDLead, count: cfFormCounts.dropped },
    { key: "setup", label: "Setup", visible: isAdmin },
  ].filter((t) => t.visible);

  const activeTab = tabs.some((t) => t.key === homeTab) ? homeTab : "dashboard";
  const cfTabConfig = CF_TAB_CONFIG[activeTab];

  return (
    <div className="home-container">
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        className="tabs-section"
      >
        <Tabs
          variant="scrollable"
          value={activeTab}
          onChange={(_, v) => dispatch(setHomeTab(v))}
        >
          {tabs.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={
                typeof t.count === "number" ? (
                  <Badge
                    badgeContent={`${t.count}`}
                    max={9999}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        borderRadius: "10px",
                        height: "18px",
                        minWidth: "18px",
                        padding: "0 6px",
                      },
                    }}
                  >
                    <span className="badgeMargin">{t.label}</span>
                  </Badge>
                ) : (
                  t.label
                )
              }
            />
          ))}
        </Tabs>
        {["BD_LEAD", "BD_REGIONAL_HEAD"].includes(role) && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setCreateModalOpen(true)}
            sx={{ mr: 1, whiteSpace: "nowrap" }}
          >
            + Create CF
          </Button>
        )}
      </Box>

      {activeTab === "dashboard" && <CfV2PipelineDashboard />}
      {activeTab === "reports" && <CfV2Report />}
      {cfTabConfig && (
        <CFV2FormsStatusTabs
          status={cfTabConfig.status}
          count={cfFormCounts[cfTabConfig.countKey]}
          refreshKey={refreshKey}
        />
      )}
      {activeTab === "setup" && <Setup />}

      {createModalOpen && (
        <CustomModal
          title="Create New CF Approval"
          onClose={() => setCreateModalOpen(false)}
          contentClassName={{ customClass: "cf-v2-create-form" }}
        >
          <CreateCfV2Form
            onClose={() => setCreateModalOpen(false)}
            onSuccess={() => {
              setCreateModalOpen(false);
              setRefreshKey((k) => k + 1);
            }}
          />
        </CustomModal>
      )}
    </div>
  );
};

export default CFApprovalv2Home;
